import { orgAPI } from "@/api/api";
import { useUpdateCanvas } from "@/api/canvas";
import { useMoveFileRecord } from "@/api/fileRecords";
import { useSetCanvasFolder } from "@/api/folders";
import type {
  ObjectActionDestinationSubmenu,
  ResolvedObjectAction,
  TransferDestination,
} from "@/object-actions/types";
import type { UnifiedItem } from "@/pages/workspaces/types";
import { moveRecordCascading } from "@/pages/workspaces/utils/folderMove";
import type { Canvas, FileRecord } from "@cohesive/api-client";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { leafNameFromPath } from "./paths";
import { useWritableWorkspaceIds } from "./useWritableWorkspaces";

/**
 * Bulk Move to for the multi-selection menu. A single destination can only be
 * expressed in one folder taxonomy, so the picker attaches per selection
 * composition:
 *
 * - files/folders only → the Library folder tree, one cascading move per item;
 * - canvases only → the canvas-folder tree, one canvas move per item;
 * - mixed (or anything with chats) → NO submenu; the action keeps its dialog
 *   handler, which is built for exactly that split-taxonomy decision.
 *
 * Commits run sequentially and report one toast: per-item toasts for a
 * ten-item move would be noise, and a partial failure names the count.
 */
export function useAttachBulkTransferSubmenu(
  actions: ResolvedObjectAction[],
  items: UnifiedItem[],
  workspaceId: string | undefined,
  workspaceFileRecords: FileRecord[],
  onMoved?: () => void,
): ResolvedObjectAction[] {
  const moveFileRecord = useMoveFileRecord(
    workspaceId ? { class: "workspace", workspace_id: workspaceId } : undefined,
  );
  const updateCanvas = useUpdateCanvas({});
  const setCanvasFolder = useSetCanvasFolder();

  const fileItems = useMemo(
    () => items.filter((i) => i.kind === "file" || i.kind === "folder"),
    [items],
  );
  const canvasItems = useMemo(
    () => items.filter((i) => i.kind === "canvas"),
    [items],
  );
  const composition: "files" | "canvases" | "mixed" =
    items.length === fileItems.length
      ? "files"
      : items.length === canvasItems.length
        ? "canvases"
        : "mixed";

  const hasBulkMove = actions.some((a) => a.id === "bulk-move");
  const writableWorkspaceIds = useWritableWorkspaceIds(
    hasBulkMove && composition !== "mixed",
  );

  const commitFiles = useCallback(
    (destination: TransferDestination) => {
      void (async () => {
        let failed = 0;
        for (const item of fileItems) {
          const record = item.data as FileRecord;
          const isFolder = record.path.endsWith("/");
          const leaf = leafNameFromPath(record.path);
          try {
            await moveRecordCascading({
              record,
              payload: {
                destination: {
                  class: "workspace",
                  workspace_id: destination.workspaceId,
                  path: `${destination.folderPath}${leaf}${isFolder ? "/" : ""}`,
                  is_folder: isFolder,
                },
              },
              allRecords: workspaceFileRecords,
              moveOne: (vars) => moveFileRecord.mutateAsync(vars),
            });
          } catch {
            failed += 1;
          }
        }
        if (failed === 0) {
          toast.success(`Moved ${fileItems.length} to ${destination.label}`);
        } else {
          toast.error(`${failed} of ${fileItems.length} failed to move.`);
        }
        onMoved?.();
      })();
    },
    [fileItems, moveFileRecord, onMoved, workspaceFileRecords],
  );

  const commitCanvases = useCallback(
    (destination: TransferDestination) => {
      void (async () => {
        let failed = 0;
        for (const item of canvasItems) {
          const canvas = item.data as Canvas;
          try {
            if (destination.workspaceId !== canvas.workspace_id) {
              const latest = await orgAPI.canvas.getById(canvas.id);
              await updateCanvas.mutateAsync({
                id: canvas.id,
                data: {
                  name: latest.name,
                  description: latest.description,
                  metadata: latest.metadata,
                  workspace_id: destination.workspaceId,
                },
              });
            }
            await setCanvasFolder.mutateAsync({
              canvasId: canvas.id,
              folderId: destination.folderId ?? null,
              workspaceId: destination.workspaceId,
            });
          } catch {
            failed += 1;
          }
        }
        if (failed === 0) {
          toast.success(`Moved ${canvasItems.length} to ${destination.label}`);
        } else {
          toast.error(`${failed} of ${canvasItems.length} failed to move.`);
        }
        onMoved?.();
      })();
    },
    [canvasItems, onMoved, setCanvasFolder, updateCanvas],
  );

  return useMemo(() => {
    if (!hasBulkMove || composition === "mixed" || items.length === 0) {
      return actions;
    }
    const submenu: ObjectActionDestinationSubmenu =
      composition === "files"
        ? {
            kind: "workspace-destination",
            operation: "move",
            commit: commitFiles,
            currentWorkspaceId: workspaceId,
            // Folders in the selection can't move into themselves.
            forbiddenPathPrefixes: fileItems
              .map((i) => (i.data as FileRecord).path)
              .filter((p) => p.endsWith("/")),
            writableWorkspaceIds,
          }
        : {
            kind: "workspace-destination",
            operation: "move",
            commit: commitCanvases,
            treeSource: "canvas-folders",
            currentWorkspaceId: workspaceId,
            writableWorkspaceIds,
          };
    return actions.map((action) =>
      action.id === "bulk-move" ? { ...action, submenu } : action,
    );
  }, [
    actions,
    commitCanvases,
    commitFiles,
    composition,
    fileItems,
    hasBulkMove,
    items.length,
    workspaceId,
    writableWorkspaceIds,
  ]);
}
